import { Flex } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { useTitle } from '@application/use_title';
import { Route, Routes } from 'react-router';
import { TopBar } from '@component/top_bar.component';
import { ScreenStart } from './screen_start/screen_start.component';
import { ViewNotFound } from '@view/view_not_found/view_not_found_route.component';
import { ScreenRfv } from './screen_rfv/screen_rfv.component';
import { CreateNewMessageController } from './view_create_new_message.controller';
import { withTranslation } from '@application/i18n/with_translation';
import { Shimmer } from '@component/new_shimmer.component';
import { Async } from '@component/async.component';
import { ScreenForms } from './screen_forms/screen_forms.component';
import { ScreenConclusion } from './screen_conclusion/mod';
import { ScreenDone } from './screen_done/screen_done.component';
import { useEffect, useState } from 'react';
import { AnalyticProvider, useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { ScreenError } from './screen_error/screen_error';

interface Props {}

/**
 * Entrypoint for Create New Message.
 *
 * **NOTE:**
 * Will isolate dependency resolution within its subtree.
 */
export const ViewCreateNewMessage = withNewContainer(function ViewCreateNewMessage(_: Props) {
	const controller = useResolveController(CreateNewMessageController);
	const analyticService = useResolve(AnalyticService);

	useTitle(withTranslation('View.CreateNewMessage.Title'));

	async function handleAbort(): Promise<void> {
		analyticService.trackEvent(AnalyticCategories.CreateNewMessage, 'Clicked', 'Abort button');

		try {
			await controller.deleteBasket();
		} catch {
			// Just continue if the request fails...
		}

		controller.goToReceptionHome();

		// TODO: Remove when angular is gone!
		if (
			(window as typeof window & { __TEMP__overrideNavigation: Record<string, unknown> | undefined })['__TEMP__overrideNavigation'] !=
			null
		) {
			window.location.reload();
		}
	}

	return (
		<AnalyticProvider category={AnalyticCategories.CreateNewMessage}>
			<Flex grow='1'>
				<TopBar.Content title={withTranslation('View.CreateNewMessage.Title')} onAbort={handleAbort} />

				<Async derive={controller.flowData} fallback={<Skeleton />} catch={<ScreenError />}>
					{() => (
						<Routes>
							<Route index element={<ViewCreateNewMessageSetup />} />
							<Route path='*' element={<ViewCreateNewMessageRoutes />} />
						</Routes>
					)}
				</Async>
			</Flex>
		</AnalyticProvider>
	);
});

function ViewCreateNewMessageSetup() {
	const controller = useResolveController(CreateNewMessageController);
	const [error, setError] = useState(false);
	const analytic = useAnalyticContext();

	useEffect(
		function whenMounted() {
			async function setupBasket() {
				const setupResult = await controller.setupNewBasket();
				if (setupResult.err) {
					setError(true);

					return;
				}

				controller.goToScreen('start');
			}

			setupBasket();
			analytic.trackEvent('Setup new basket');
		},
		[controller, analytic],
	);

	if (error) {
		return <ScreenError />;
	}

	return <Skeleton />;
}

function ViewCreateNewMessageRoutes() {
	const controller = useResolveController(CreateNewMessageController);
	const analyticCtx = useAnalyticContext();

	useEffect(
		function whenMounted() {
			controller.loadSettings();

			analyticCtx.trackEvent('Load settings');
		},
		[controller, analyticCtx],
	);

	if (controller.flowSettings.value.flowState === 'pending') {
		return <Skeleton />;
	}

	return (
		<Routes>
			<Route path='/start'>
				<Route index element={<ScreenStart />} />
			</Route>
			<Route path='/reason-for-visit'>
				<Route index element={<ScreenRfv />} />
			</Route>
			<Route path='/forms/*' element={<ScreenForms />} />
			<Route path='/conclusion'>
				<Route index element={<ScreenConclusion />} />
			</Route>
			<Route path='/done' element={<ScreenDone />} />
			<Route path='/done/:conversationId' element={<ScreenDone />} />
			<Route path='/error'>
				<Route index element={<ScreenError />} />
			</Route>
			<Route path='/*' element={<ViewNotFound />} />
		</Routes>
	);
}

export function Skeleton() {
	return (
		<Flex gap={tokens.spacing.x3} grow='1'>
			<Shimmer height={40} width='50%' />

			<Flex gap={tokens.spacing.x0_5}>
				<Shimmer height={15} width='30%' />
				<Shimmer height={15} width='80%' />
				<Shimmer height={15} width='60%' />
				<Shimmer height={15} width='70%' />
			</Flex>
		</Flex>
	);
}
